import { useActiveRightStackJid } from '@/app/store/navigationSlice';
import { IconCreateGroup, IconEmptyPlaceholderChat } from '@/assets/images';
import { AppIcon, ListTile, ListTileSkeleton, SearchBox, SmartListView } from '@/components';
import EmptyPlaceholder from '@/components/EmptyPlaceholder';
import { MessageRequestsBanner } from '@/features/invite/ui';
import { useLanguage } from '@/localization/useLanguage';
import React, { useRef } from 'react';
import { formatUserIdToJid } from '../../utils/jidIdFormat';
import { useRecentChatList } from '../../viewmodels';
import ArchivedChatEntry from '../ArchivedChatList/ArchivedChatEntry';
import ChatItem from './ChatItem';
import './recentChatList.scss';

/**
 * Pure rendering component — zero business logic.
 * Navigation actions and feature gates come from useRecentChatList().
 */
const RecentChatList = () => {
   const { t } = useLanguage();
   const containerRef = useRef(null);
   const activeChatJid = useActiveRightStackJid();
   const {
      handleNewChat,
      isSearchEnabled,
      UX_CONFIG,
      loading,
      loadMore,
      chats,
      hasMore,
      updateSearchText,
      searchText,
      isArchived,
      inviteUserCount,
      handleOpenMessageRequests,
   } = useRecentChatList();

   const renderLoaderView = (count = 1) =>
      Array.from({ length: count }).map((_, i) => <ListTileSkeleton key={`skeleton-${i * 2 + 1}`} />);

   const renderEmptyComponent = () => {
      if (loading) return null;
      const isSearchActive = !!searchText;

      return (
         <EmptyPlaceholder
            icon={isSearchActive ? '' : <IconEmptyPlaceholderChat />}
            title={isSearchActive ? t('chat.NO_SEARCH_RESULTS') : t('chat.EMPTY_TITLE')}
            description={isSearchActive ? '' : t('chat.EMPTY_DESCRIPTION')}
            actionText={isSearchActive ? '' : t('chat.START_CHAT_BTN')}
            onAction={isSearchActive ? null : handleNewChat}
            className="side-nav-btn"
            actionbutton={!isSearchActive}
         />
      );
   };

   let skeletonCount = 1;
   if (chats.length === 0) {
      skeletonCount = 20;
   }
   const loadingBottomComponent = (
      <>
         {loading ? renderLoaderView(skeletonCount) : null}
         {!isArchived && <ArchivedChatEntry />}
      </>
   );

   const listHeaderComponent = (
      <>
         <MessageRequestsBanner count={inviteUserCount} onClick={handleOpenMessageRequests} />
         {isArchived && <ArchivedChatEntry />}
      </>
   );

   return (
      <div className="recent-chat-list ct-flex ct-fcol">
         <ListTile
            as="div"
            className="main-chat-header"
            title={<h3 className="chat-name">{t('chat.CHATS_TITLE')}</h3>}
            trailing={
               <div className="ct-flex ct-aiCenter ct-gap12">
                  <AppIcon
                     size="lg"
                     className="cursor-pointer creategroup"
                     icon={<IconCreateGroup />}
                     color="black"
                     onClick={handleNewChat}
                  />
               </div>
            }
         />
         {isSearchEnabled && (
            <div className="recent-chat-list-searchbox">
               <SearchBox scopeKey="recentchat" onChange={updateSearchText} placeholder={t('common.SEARCH')} />
            </div>
         )}

         <SmartListView
            id="recentchatlist"
            ref={containerRef}
            data={chats}
            renderItem={({ item, index }) => {
               const prevItem = index > 0 ? chats[index - 1] : null;
               const isPrevActive =
                  prevItem && activeChatJid === formatUserIdToJid(prevItem.fromUserId, prevItem.chatType);
               return <ChatItem item={item} index={index} isPrevActive={isPrevActive} containerId={'recentchatlist'} />;
            }}
            keyExtractor={item => item.fromUserId}
            itemHeight={70}
            onLoadBottom={loadMore}
            hasMoreBottom={hasMore}
            hasMoreTop={false}
            debounceMs={100}
            loadingBottom={loading}
            LoadingBottomComponent={loadingBottomComponent}
            paginationTriggerDelay={UX_CONFIG.paginationTriggerDelay}
            autoLoadCheckDelay={UX_CONFIG.autoLoadCheckDelay}
            ListEmptyComponent={renderEmptyComponent()}
            ListHeaderComponent={listHeaderComponent}
         />
      </div>
   );
};

export default React.memo(RecentChatList);
